import React from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { axiosget } from "../../helpers/httprequest";
import Loader from "../../components/Loader";
import { Alert, AlertDescription } from "../../components/ui/Alert";
import QRCode from "react-qr-code";
import is_money from "../../helpers/is_money";
import is_null from "../../helpers/is_null";
import { localRequest } from "../../helpers/uri";

const ApplicationReceipt = () => {
  const { id } = useParams();

  // Fetch receipt data
  const { data, isLoading, error } = useQuery({
    queryKey: ["applicationReceiptInfo", id],
    queryFn: async () => {
      const req = await axiosget(`ms_application_fee/receipt/${id}`);
      return req?.data;
    },
  });

  // Fetch system settings (institution name, logo, address, affiliate)
  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ["systemSettings"],
    queryFn: async () => {
      const req = await axiosget("ms_system_settings/public");
      return req?.data;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  // Check if everything is fully loaded
  const isFullyLoaded = !isLoading && !settingsLoading && data && !data?.message;

  // Auto print only when fully loaded
  if (isFullyLoaded && !is_null(data)) {
    setTimeout(() => window.print(), 500);
  }

  const isPaid = data?.find?.isverified === true;

  // Dynamic values from settings with fallbacks
  const institutionName = settings?.institutionName || "Adamu Tafawa Balewa College of Education, Kangere";
  const shortName = settings?.shortName || "ATBCOE";
  const address = settings?.address || "Kangere, Bauchi State, Nigeria";
  const affiliate = settings?.affiliate || "";
  const logoUrl = settings?.logo ? `${localRequest}${settings.logo.replace(/^\//, '')}` : "/images/college-logo.png";

  // Show loader while any data is still loading
  if (isLoading || settingsLoading) {
    return <Loader />;
  }

  return (
    <>
      {error && <Alert variant="danger"><AlertDescription>{`Local server error: ${error}`}</AlertDescription></Alert>}
      {data?.message !== undefined && <Alert variant="danger"><AlertDescription>{data?.message}</AlertDescription></Alert>}
      {isFullyLoaded && (
        <div
          id="printDocument"
          style={{
            maxWidth: "800px",
            margin: "0 auto",
            padding: "20px",
            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
            position: "relative",
          }}
        >
          {/* Watermark */}
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%) rotate(-30deg)",
              opacity: 0.08,
              fontSize: "120px",
              fontWeight: "bold",
              color: isPaid ? "#16a34a" : "#dc2626",
              pointerEvents: "none",
              whiteSpace: "nowrap",
              zIndex: 0,
            }}
          >
            {isPaid ? "PAID" : "UNPAID"}
          </div>

          {/* Receipt Card */}
          <div
            style={{
              background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
              borderRadius: "16px",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
              overflow: "hidden",
              position: "relative",
              zIndex: 1,
            }}
          >
            {/* Green Header Bar */}
            <div
              style={{
                background: "linear-gradient(135deg, #16a34a 0%, #059669 50%, #0d9488 100%)",
                padding: "20px 30px",
                color: "white",
                textAlign: "center",
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "15px" }}>
                <img
                  src={logoUrl}
                  alt="Institution Logo"
                  style={{
                    width: "70px",
                    height: "70px",
                    borderRadius: "50%",
                    border: "3px solid rgba(255,255,255,0.3)",
                    objectFit: "cover",
                  }}
                  onError={(e) => { e.target.onerror = null; e.target.src = "/images/college-logo.png"; }}
                />
                <div>
                  <h1 style={{ margin: 0, fontSize: "24px", fontWeight: "700", letterSpacing: "0.5px" }}>
                    {institutionName}
                  </h1>
                  <p style={{ margin: "5px 0 0 0", fontSize: "14px", opacity: 0.9 }}>
                    {address}
                  </p>
                </div>
              </div>
            </div>

            {/* Receipt Title */}
            <div
              style={{
                background: "linear-gradient(90deg, #f0fdf4 0%, #dcfce7 50%, #f0fdf4 100%)",
                padding: "15px 30px",
                borderBottom: "2px solid #16a34a",
                textAlign: "center",
              }}
            >
              <h2 style={{ margin: 0, color: "#16a34a", fontSize: "18px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "2px" }}>
                ✓ Payment Receipt - Bursary Department
              </h2>
            </div>

            {/* Receipt Body */}
            <div style={{ padding: "30px", display: "flex", gap: "30px" }}>
              {/* Left - Details */}
              <div style={{ flex: 1 }}>
                {/* Student Info Section */}
                <div style={{ marginBottom: "25px" }}>
                  <h3 style={{ color: "#16a34a", fontSize: "14px", fontWeight: "600", marginBottom: "15px", textTransform: "uppercase", letterSpacing: "1px", borderBottom: "2px solid #e5e7eb", paddingBottom: "8px" }}>
                    Student Information
                  </h3>

                  <div style={{ display: "grid", gap: "12px" }}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span style={{ color: "#6b7280", fontSize: "13px", width: "130px" }}>Full Name:</span>
                      <span style={{ fontWeight: "600", color: "#1f2937", fontSize: "15px" }}>
                        {data?.bio?.firstname} {data?.bio?.middlename} {data?.bio?.lastname}
                      </span>
                    </div>

                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span style={{ color: "#6b7280", fontSize: "13px", width: "130px" }}>Admission Code:</span>
                      <span style={{ fontWeight: "600", color: "#1f2937", fontSize: "15px", background: "#f3f4f6", padding: "4px 12px", borderRadius: "6px" }}>
                        {data?.find?.application?.code?.replace("ATBCOE/", "")}
                      </span>
                    </div>

                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span style={{ color: "#6b7280", fontSize: "13px", width: "130px" }}>Programme:</span>
                      <span style={{ fontWeight: "600", color: "#1f2937", fontSize: "14px" }}>
                        {data?.find?.programme?.code} - {data?.find?.programme?.name}
                      </span>
                    </div>

                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span style={{ color: "#6b7280", fontSize: "13px", width: "130px" }}>Email:</span>
                      <span style={{ color: "#1f2937", fontSize: "14px" }}>{data?.find?.applicant?.emailaddress}</span>
                    </div>

                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span style={{ color: "#6b7280", fontSize: "13px", width: "130px" }}>Phone:</span>
                      <span style={{ color: "#1f2937", fontSize: "14px" }}>{data?.find?.applicant?.phonenumber}</span>
                    </div>
                  </div>
                </div>

                {/* Payment Info Section */}
                <div>
                  <h3 style={{ color: "#16a34a", fontSize: "14px", fontWeight: "600", marginBottom: "15px", textTransform: "uppercase", letterSpacing: "1px", borderBottom: "2px solid #e5e7eb", paddingBottom: "8px" }}>
                    Payment Information
                  </h3>

                  <div style={{ display: "grid", gap: "12px" }}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span style={{ color: "#6b7280", fontSize: "13px", width: "130px" }}>Amount:</span>
                      <span style={{ fontWeight: "700", color: "#16a34a", fontSize: "20px" }}>
                        {is_money(data?.find?.amount)}
                      </span>
                    </div>

                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span style={{ color: "#6b7280", fontSize: "13px", width: "130px" }}>Status:</span>
                      <span
                        style={{
                          fontWeight: "600",
                          fontSize: "13px",
                          padding: "6px 16px",
                          borderRadius: "20px",
                          background: isPaid ? "#dcfce7" : "#fee2e2",
                          color: isPaid ? "#16a34a" : "#dc2626",
                        }}
                      >
                        {isPaid ? "✓ PAYMENT VERIFIED" : "✗ NOT PAID"}
                      </span>
                    </div>

                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span style={{ color: "#6b7280", fontSize: "13px", width: "130px" }}>Date:</span>
                      <span style={{ color: "#1f2937", fontSize: "14px" }}>
                        {new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right - QR Code */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "20px", background: "#f8fafc", borderRadius: "12px", minWidth: "180px" }}>
                <QRCode
                  value={btoa(
                    JSON.stringify({
                      rrr: data?.RRR,
                      name: data?.details?.payerName,
                    })
                  )}
                  size={140}
                  style={{ padding: "10px", background: "white", borderRadius: "8px" }}
                />
                <span style={{ marginTop: "10px", fontSize: "11px", color: "#6b7280", textAlign: "center" }}>Scan to verify</span>
              </div>
            </div>

            {/* RRR Section */}
            <div
              style={{
                background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                padding: "25px 30px",
                textAlign: "center",
                borderTop: "1px dashed #e5e7eb",
              }}
            >
              <p style={{ margin: "0 0 8px 0", fontSize: "12px", color: "#6b7280", textTransform: "uppercase", letterSpacing: "2px" }}>
                Payment Reference (RRR)
              </p>
              <div
                style={{
                  fontSize: "48px",
                  fontWeight: "800",
                  color: "#1f2937",
                  letterSpacing: "4px",
                  fontFamily: "'Courier New', monospace",
                  background: "white",
                  display: "inline-block",
                  padding: "15px 40px",
                  borderRadius: "12px",
                  border: "3px solid #16a34a",
                  boxShadow: "0 4px 15px rgba(22, 163, 74, 0.15)",
                }}
              >
                {data?.find?.paymentReference}
              </div>
              <p style={{ margin: "15px 0 0 0", fontSize: "13px", color: "#6b7280" }}>
                {data?.find?.trxdata?.description}
              </p>
            </div>

            {/* Footer */}
            <div
              style={{
                background: "#1f2937",
                color: "white",
                padding: "15px 30px",
                textAlign: "center",
                fontSize: "12px",
              }}
            >
              <p style={{ margin: 0, opacity: 0.8 }}>
                This is an official payment receipt from {institutionName}
              </p>
              <p style={{ margin: "5px 0 0 0", opacity: 0.6, fontSize: "11px" }}>
                {address}
              </p>
            </div>
          </div>

          {/* Print Styles */}
          <style>{`
            @media print {
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              #printDocument { max-width: 100% !important; padding: 0 !important; }
            }
          `}</style>
        </div>
      )}
    </>
  );
};

export default ApplicationReceipt;
